import { useState } from 'react';
import { submitContactForm } from '@/lib/api/forms';
import { FormShell, type FormStatus } from './FormShell';
import { SmsConsentDisclosure } from './SmsConsentDisclosure';
import { Honeypot } from './Honeypot';
import { useHoneypot } from './use-honeypot';

export function FormContact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [smsConsent, setSmsConsent] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const { value: hp, honeypotProps } = useHoneypot();

  const onChange =
    (key: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await submitContactForm(
        {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
        },
        'contact_message',
        smsConsent,
        hp,
      );
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', message: '' });
      setSmsConsent(false);
    } catch {
      setStatus('error');
    }
  };

  return (
    <FormShell
      eyebrow="// SEND US A MESSAGE"
      status={status}
      successTitle="MESSAGE RECEIVED"
      successBody="We'll respond within one business hour."
    >
      <form onSubmit={onSubmit} method="post" className="space-y-6" noValidate={false}>
        <Honeypot {...honeypotProps} />
        <div className="grid sm:grid-cols-2 gap-6">
          <input
            className="bold-input"
            name="name"
            id="contact_name"
            placeholder="FULL NAME"
            required
            autoComplete="name"
            value={form.name}
            onChange={onChange('name')}
          />
          <input
            className="bold-input"
            name="phone"
            id="contact_phone"
            type="tel"
            placeholder="PHONE"
            required
            autoComplete="tel"
            value={form.phone}
            onChange={onChange('phone')}
          />
        </div>

        <input
          className="bold-input"
          name="email"
          id="contact_email"
          type="email"
          placeholder="EMAIL"
          required
          autoComplete="email"
          value={form.email}
          onChange={onChange('email')}
        />

        <textarea
          className="bold-input min-h-[120px] resize-none"
          name="message"
          id="contact_message"
          placeholder="HOW CAN WE HELP?"
          required
          maxLength={500}
          value={form.message}
          onChange={onChange('message')}
        />

        <label htmlFor="sms_consent" className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            id="sms_consent"
            name="sms_consent"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="mt-1 h-4 w-4 accent-thunder cursor-pointer flex-shrink-0"
          />
          <SmsConsentDisclosure />
        </label>

        <button
          type="submit"
          disabled={status === 'sending'}
          className="w-full pulse-btn bg-thunder hover:bg-thunder-dark disabled:opacity-60 disabled:cursor-not-allowed text-charcoal-deep font-display font-bold py-5 tracking-[0.3em] text-sm transition"
        >
          {status === 'sending' ? 'SENDING…' : 'SEND MESSAGE →'}
        </button>
      </form>
    </FormShell>
  );
}
